import { jsx as _jsx } from "react/jsx-runtime";
import React, { lazy, Suspense } from 'react';
import styled from 'styled-components';
import { CurrencyFlagSizes } from 'components/atoms/currency/types';
import { Input } from 'components/facade/input';
import { useAntdRlUISwitcher } from 'contexts/antd-rlui-switcher';
const LazyCurrencyFlag = /*#__PURE__*/ lazy(()=>import('react-currency-flags').then((m)=>({
            default: m.default
        })));
const currencyFlagFallbackStyle = (size)=>{
    switch(size){
        case CurrencyFlagSizes.MEDIUM:
            return {
                width: 20,
                height: 15
            };
        case CurrencyFlagSizes.LARGE:
            return {
                width: 24,
                height: 18
            };
        case CurrencyFlagSizes.XLARGE:
            return {
                width: 32,
                height: 24
            };
        case CurrencyFlagSizes.SMALL:
        default:
            return {
                width: 16,
                height: 12
            };
    }
};
export var CurrencyPrefixNotation = /*#__PURE__*/ function(CurrencyPrefixNotation) {
    CurrencyPrefixNotation["CurrencySymbol"] = "CURRENCY_SYMBOL";
    CurrencyPrefixNotation["CurrencyCode"] = "CURRENCY_CODE";
    CurrencyPrefixNotation["CurrencyCodeAndSymbol"] = "CURRENCY_CODE_SYMBOL";
    return CurrencyPrefixNotation;
}({});
const K = 10000;
const M = 1000000;
const B = 1000000000;
export const DEFAULT_CURRENCY_COMPACT_SUFFIXES = [
    'K',
    'M',
    'B'
];
const getAllowedCompactSuffixes = (compactSuffixes)=>compactSuffixes === undefined ? DEFAULT_CURRENCY_COMPACT_SUFFIXES : compactSuffixes.length === 0 ? [] : DEFAULT_CURRENCY_COMPACT_SUFFIXES.filter((letter)=>compactSuffixes.some((s)=>String(s).toUpperCase() === letter));
/** Same digit grouping / fractional slicing as `currencyRenderer`, without K/M/B logic. */ const buildFormatterWithoutRounding = (precision, showTrailingZeros)=>{
    const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: showTrailingZeros ? precision : 0,
        maximumFractionDigits: precision
    });
    return (value = 0)=>{
        const [wholePart, fractionalPart] = value.toString().split('.');
        const formattedWhole = formatter.format(Number(wholePart));
        if (precision <= 0 || fractionalPart == null) {
            return formattedWhole;
        }
        let slicedFraction = fractionalPart.slice(0, precision);
        if (slicedFraction.length === 0) {
            return formattedWhole;
        }
        // Strip any decimals the formatter added to the whole part (e.g. "10.00"
        // from showTrailingZeros) since we append the real fraction below.
        const formattedWholeOnly = formattedWhole.split('.')[0];
        if (showTrailingZeros) {
            slicedFraction = slicedFraction.padEnd(precision, '0');
        }
        return [
            formattedWholeOnly,
            slicedFraction
        ].join('.');
    };
};
export const defaultCurrency = {
    currencyCode: 'USD',
    currencySymbol: '$',
    currencyName: 'US Dollar'
};
const removeTrailingZeroes = (amount)=>amount.replace(/\.0+$/, '');
export const numberFormatter = (amount, precision = 2)=>Number(removeTrailingZeroes(amount.toFixed(precision)));
export const getCurrencyPrefix = ({ currencyCode, currencySymbol } = defaultCurrency, notation = "CURRENCY_CODE")=>{
    let prefixString = '';
    switch(notation){
        case "CURRENCY_SYMBOL":
            prefixString = currencySymbol;
            break;
        case "CURRENCY_CODE":
            prefixString = `${currencyCode} `;
            break;
        case "CURRENCY_CODE_SYMBOL":
            prefixString = `${currencyCode} ${currencySymbol}`;
            break;
        default:
            break;
    }
    return prefixString;
};
const BaseCurrencyInput = styled(Input)`
  input::placeholder {
    color: var(--black-100);
  }
`;
const addCurrencyPrefix = (val, currency, showCurrencyPrefix = true, notation)=>{
    if (val == null || Number.isNaN(val)) {
        return '';
    }
    if (!showCurrencyPrefix) {
        return val;
    }
    const prefixString = getCurrencyPrefix(currency, notation);
    return `${prefixString}${val}`;
};
export const currencyRenderer = (amount = 0, currency = defaultCurrency, compactNotation = true, showCurrencyPrefix = true, precision = 2, notation = "CURRENCY_CODE", showTrailingZeros = false, compactSuffixes)=>{
    const formatterWithoutRounding = buildFormatterWithoutRounding(precision, showTrailingZeros);
    const absoluteAmount = Math.abs(amount);
    const allowedSuffixes = getAllowedCompactSuffixes(compactSuffixes);
    if (compactNotation) {
        if (allowedSuffixes.includes('K') && absoluteAmount >= K && absoluteAmount < M) {
            const formattedAmount = formatterWithoutRounding(amount / 1000);
            return `${addCurrencyPrefix(formattedAmount, currency, showCurrencyPrefix, notation)}K`;
        }
        if (allowedSuffixes.includes('M') && absoluteAmount >= M && absoluteAmount < B) {
            const formattedAmount = formatterWithoutRounding(amount / M);
            return `${addCurrencyPrefix(formattedAmount, currency, showCurrencyPrefix, notation)}M`;
        }
        if (allowedSuffixes.includes('B') && absoluteAmount >= B) {
            const formattedAmount = formatterWithoutRounding(amount / B);
            return `${addCurrencyPrefix(formattedAmount, currency, showCurrencyPrefix, notation)}B`;
        }
    }
    return `${addCurrencyPrefix(formatterWithoutRounding(amount), currency, showCurrencyPrefix, notation)}`;
};
export const currencyFormatterForInput = (amount, currency, compactNotation = true, showCurrencyPrefix = true, precision = 2, notation = "CURRENCY_CODE")=>{
    if (amount == null) {
        return '';
    }
    if (typeof amount === 'string') {
        const [wholePart, fractionalPart] = amount.split('.');
        return [
            `${currencyRenderer(Number(wholePart !== null && wholePart !== void 0 ? wholePart : 0), currency, compactNotation, showCurrencyPrefix, precision, notation)}`,
            ...fractionalPart != null ? [
                fractionalPart
            ] : []
        ].join('.');
    }
    return currencyRenderer(amount, currency, compactNotation, showCurrencyPrefix, precision, notation);
};
export const CurrencyFlagWrapper = styled.div`
  display: flex;
  align-items: center;

  > span {
    border-radius: 1.5px;
    box-shadow: 0px 0.2px 0.5px 0px rgba(0, 0, 0, 0.15) inset;
  }
`;
const CurrencyFlagRendererComponent = ({ currency, size, height, width })=>/*#__PURE__*/ _jsx(CurrencyFlagWrapper, {
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx("span", {
                style: {
                    ...currencyFlagFallbackStyle(size),
                    display: 'inline-block',
                    flexShrink: 0,
                    verticalAlign: 'middle'
                },
                "aria-hidden": true
            }),
            children: /*#__PURE__*/ _jsx(LazyCurrencyFlag, {
                currency: currency,
                size: size,
                height: height,
                width: width
            })
        })
    });
export const CurrencyFlagRenderer = /*#__PURE__*/ React.memo(CurrencyFlagRendererComponent);
export const StyledCurrencyInput = (props)=>{
    const isNewDesignEnabled = useAntdRlUISwitcher();
    const { size, ...restProps } = props;
    const finalSize = isNewDesignEnabled && size == null ? 'middle' : size;
    return /*#__PURE__*/ _jsx(BaseCurrencyInput, {
        ...restProps,
        size: finalSize
    });
};
